// One RFQ file, open in a panel beside the RFQ (Lane 1399, Ryan 2026-10-07:
// "instead of just downloading it, it slid in from the side"). PDFs and
// pictures show as they are, TIFF scans as PNG pages, spreadsheets, emails and
// Word files as the text Wingman read (table rows as a table). Zip files and
// CAD drawings are download only. `highlight` marks the rows a line came from:
// each is a spreadsheet row ({sheet, row}) or a row's text (email and Word
// tables carry no row numbers).
import React, { useEffect, useMemo, useState } from 'react';

const ROW_PREFIX = /^Row (\d+): /;

// Lines of one section as blocks: runs of table rows (cells split by tabs)
// become a table, other lines stay text.
function blocks(text) {
  const out = [];
  for (const raw of text.split('\n')) {
    const m = raw.match(ROW_PREFIX);
    const body = m ? raw.slice(m[0].length) : raw;
    const cells = body.includes('\t') || m ? body.split('\t') : null;
    const last = out[out.length - 1];
    if (cells) {
      const row = { row: m ? Number(m[1]) : null, cells, text: body };
      if (last && last.type === 'table') last.rows.push(row); else out.push({ type: 'table', rows: [row] });
    } else {
      out.push({ type: 'text', text: body });
    }
  }
  return out;
}

const SHEET_LABEL = /^sheet \d+ \((.*)\)$/;

const marked = (text, cells, highlight, sheet, row) => highlight.some((h) => (
  typeof h === 'object' && h !== null
    ? h.sheet != null && h.sheet === sheet && h.row === row
    : h === text || (cells || []).some((c) => c.trim() === h)));

function TextView({ sections, highlight, cut }) {
  const first = React.useRef(null);
  useEffect(() => { if (first.current) first.current.scrollIntoView({ block: 'center' }); }, [sections, highlight]);
  let placed = false;
  const ref = () => { if (placed) return undefined; placed = true; return first; };
  return (
    <div className="qtng-fp-text">
      {sections.map((s, si) => {
        const sheet = (s.section.match(SHEET_LABEL) || [])[1] ?? null;
        return (
        <div key={si}>
          {sections.length > 1 && <div className="qtng-fp-sec">{s.section}</div>}
          {blocks(s.text).map((b, bi) => (b.type === 'table' ? (
            <div key={bi} className="qtng-fp-scroll">
              <table className="qtng-fp-table"><tbody>
                {b.rows.map((r, ri) => {
                  const hit = marked(r.text, r.cells, highlight, sheet, r.row);
                  return (
                    <tr key={ri} className={hit ? 'qtng-fp-hit' : undefined} ref={hit ? ref() : undefined}>
                      {r.row != null && <td className="qtng-fp-rn">{r.row}</td>}
                      {r.cells.map((c, ci) => <td key={ci}>{c}</td>)}
                    </tr>
                  );
                })}
              </tbody></table>
            </div>
          ) : (
            <div key={bi} className={`qtng-fp-line ${marked(b.text, null, highlight) ? 'qtng-fp-hit' : ''}`}
              ref={marked(b.text, null, highlight) ? ref() : undefined}>{b.text || ' '}</div>
          )))}
        </div>
        );
      })}
      {cut && <div className="qtng-mut qtng-small">The file is longer than shown here. Download it to see all of it.</div>}
    </div>
  );
}

export default function QuotingFilePanel({ api, rfqId, file, onClose, onDownload }) {
  const { attachment, page = null, highlight = [] } = file;
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [url, setUrl] = useState(null);
  const [tiffPage, setTiffPage] = useState(page || 1);
  const id = attachment.rfq_attachment_id;

  useEffect(() => {
    let alive = true;
    setView(null); setError('');
    api.view(rfqId, id).then((v) => { if (alive) setView(v); })
      .catch(() => { if (alive) setError('Could not open the file. Try again or download it.'); });
    return () => { alive = false; };
  }, [api, rfqId, id]);
  // Every open request starts at its own page, even the same link again.
  useEffect(() => { setTiffPage(page || 1); }, [file, page]);

  // The bytes of a PDF or picture, as a typed blob the browser can show.
  useEffect(() => {
    if (!view || !['pdf', 'image', 'tiff'].includes(view.kind)) return undefined;
    let alive = true;
    let made = null;
    const load = view.kind === 'tiff'
      ? api.tiffPage(rfqId, id, Math.min(tiffPage, view.pages || 1))
      : api.download(rfqId, id).then((b) => new Blob([b], { type: view.content_type }));
    load.then((blob) => {
      if (!alive) return;
      made = URL.createObjectURL(blob);
      setUrl(made);
    }).catch(() => { if (alive) setError('Could not open the file. Try again or download it.'); });
    return () => { alive = false; if (made) URL.revokeObjectURL(made); setUrl(null); };
  }, [api, rfqId, id, view, tiffPage]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const name = attachment.original_filename
    || (String(attachment.detected_content_type || '').startsWith('text/') ? 'Email text' : 'Unnamed file');
  const body = useMemo(() => {
    if (error) return <div className="qtng-err">{error}</div>;
    if (!view) return <div className="qtng-empty">Loading…</div>;
    if (view.kind === 'download') {
      return <div className="qtng-empty">{view.reason === 'no_text' ? 'Wingman has no text of this file to show.' : 'This kind of file cannot be shown here.'} Download it to open it.</div>;
    }
    if (view.kind === 'text') return <TextView sections={view.sections || []} highlight={highlight} cut={view.cut} />;
    if (!url) return <div className="qtng-empty">Loading…</div>;
    if (view.kind === 'pdf') return <iframe className="qtng-fp-frame" title={name} src={page ? `${url}#page=${page}` : url} />;
    return (
      <div className="qtng-fp-img">
        {view.kind === 'tiff' && view.pages > 1 && (
          <div className="qtng-acctrow">
            <button type="button" className="qtng-btn qtng-btn--small" disabled={tiffPage <= 1} onClick={() => setTiffPage((n) => n - 1)}>Previous page</button>
            <span className="qtng-mut">Page {tiffPage} of {view.pages}{view.more_pages ? '+' : ''}</span>
            <button type="button" className="qtng-btn qtng-btn--small" disabled={tiffPage >= view.pages} onClick={() => setTiffPage((n) => n + 1)}>Next page</button>
          </div>
        )}
        {view.kind === 'tiff' && view.more_pages && <div className="qtng-mut qtng-small">More pages than shown here. Download the file to see all of them.</div>}
        <img src={url} alt={name} />
      </div>
    );
  }, [error, view, url, page, name, highlight, tiffPage]);

  return (
    <aside className="qtng-fp" aria-label={`File: ${name}`}>
      <div className="qtng-fp-head">
        <span className="qtng-cut" title={name}>{name}</span>
        <span className="qtng-fp-actions">
          <button type="button" className="qtng-btn qtng-btn--small" onClick={() => onDownload(attachment)}>Download</button>
          <button type="button" className="qtng-btn qtng-btn--small" onClick={onClose} aria-label="Close the file">Close</button>
        </span>
      </div>
      <div className="qtng-fp-body">{body}</div>
    </aside>
  );
}
